import React, { useEffect } from 'react';
import { Plus, Filter, ChevronUp, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';
import QawaemPageTitleWithFilters from '../../../products/saudi/components/QawaemPageTitleWithFilters';
import QawaemStatsCard from '../../../products/saudi/components/QawaemStatsCard';
import QawaemTableToolbarSearch from '../../../products/saudi/components/QawaemTableToolbarSearch';
import QawaemTableCardsViewToggle from '../../../products/saudi/components/QawaemTableCardsViewToggle';
import QawaemImportExportToolbar from '../../../products/saudi/components/QawaemImportExportToolbar';
import {
  QAWAEM_KPI_GRID_CLASS,
  sliceQawaemStats,
} from '../../../products/saudi/components/QawaemErpListPageShell';

export { QAWAEM_KPI_GRID_CLASS, sliceQawaemStats };

/** Table thead class — column titles only (glass-table-head for Qawaem). */
export function facturationTableHeadClass(qawaem = isSaudiBrand()) {
  return qawaem ? 'glass-table-head' : 'bg-white/5';
}

/** Table scroll wrapper class matching المنتجات. */
export function facturationTableScrollClass(qawaem = isSaudiBrand()) {
  return qawaem ? 'glass-table-scroll overflow-x-auto' : 'overflow-x-auto rounded-b-3xl';
}

/** Glass vs legacy table shell wrapper. */
export function facturationTableShellClass(qawaem = isSaudiBrand()) {
  return qawaem
    ? 'glass-panel glass-table-shell overflow-visible'
    : 'bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-visible';
}

/**
 * Dual-brand list layout matching المنتجات (Products):
 * Qawaem title-row filters, 4 KPI cards, header import/export;
 * legacy brand keeps advanced options + controls strip inside table shell.
 */
export default function FacturationListPageLayout({
  title,
  subtitle,
  icon: Icon,
  stats = [],
  viewMode = 'table',
  onViewModeChange,
  filterActions = null,
  searchTerm = '',
  onSearchChange,
  searchPlaceholder,
  columns,
  visibleColumnCount,
  totalColumnCount,
  onCreate,
  createLabel,
  importExport,
  headerActions = null,
  showAdvancedOptions = false,
  onToggleAdvancedOptions,
  advancedOptionsLabel,
  advancedOptionsContent = null,
  controlsStrip = null,
  bulkActionsBar = null,
  children,
  footer = null,
  className = 'space-y-6',
  containerClassName = 'employees-container',
}) {
  const { t } = useTranslation();
  const qawaem = isSaudiBrand();
  const displayStats = sliceQawaemStats(stats, 4);

  useEffect(() => {
    if (viewMode === 'table' && onSearchChange && String(searchTerm || '').trim()) {
      onSearchChange('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const kpiCards = displayStats.map((stat, i) => ({
    key: stat.key || stat.label || stat.title || i,
    label: stat.label || stat.title,
    value: stat.value,
    icon: stat.icon,
    variant: stat.variant || (i % 2 === 0 ? 'copper' : 'forest'),
    className: stat.className,
  }));

  return (
    <div className={`${className} ${containerClassName}`}>
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          {qawaem ? (
            <>
              <QawaemPageTitleWithFilters
                titleSuffix={onViewModeChange ? (
                  <QawaemTableCardsViewToggle value={viewMode} onChange={onViewModeChange} />
                ) : null}
                filters={
                  <>
                    {filterActions || bulkActionsBar}
                    {onSearchChange ? (
                      <QawaemTableToolbarSearch
                        viewMode={viewMode}
                        value={searchTerm}
                        onChange={onSearchChange}
                        placeholder={searchPlaceholder || t('common.search', { defaultValue: 'بحث...' })}
                      />
                    ) : null}
                  </>
                }
              >
                <h1 className="text-3xl font-bold text-forest-900 flex items-center gap-3">
                  {Icon ? <Icon className="page-title-icon page-title-icon--bronze h-8 w-8" /> : null}
                  {title}
                </h1>
              </QawaemPageTitleWithFilters>
              {subtitle ? <p className="page-section-desc">{subtitle}</p> : null}
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                {Icon ? <Icon className="h-8 w-8 text-blue-400" /> : null}
                {title}
              </h1>
              {subtitle ? <p className="text-white/60 mt-2">{subtitle}</p> : null}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!qawaem ? (
            <>
              {onToggleAdvancedOptions ? (
                <button
                  type="button"
                  onClick={onToggleAdvancedOptions}
                  className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
                >
                  <Filter className="h-5 w-5" />
                  <span className="hidden sm:inline">{advancedOptionsLabel}</span>
                  {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              ) : null}
              {onCreate ? (
                <button
                  type="button"
                  className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
                  onClick={onCreate}
                >
                  <Plus className="h-5 w-5" />
                  <span>{createLabel}</span>
                </button>
              ) : null}
              {headerActions}
            </>
          ) : (
            <>
              {headerActions}
              {onCreate ? (
                <button type="button" className="btn-stat-green btn-header-compact shrink-0" onClick={onCreate}>
                  <span className="hidden sm:inline">{createLabel}</span>
                  <Plus className="h-4 w-4" />
                </button>
              ) : null}
              {importExport ? <QawaemImportExportToolbar compact {...importExport} /> : null}
            </>
          )}
        </div>
      </div>

      {qawaem ? (
        kpiCards.length ? (
          <div className={QAWAEM_KPI_GRID_CLASS}>
            {kpiCards.map((stat, i) => (
              <QawaemStatsCard
                key={stat.key || stat.label || i}
                label={stat.label}
                value={stat.value}
                icon={stat.icon}
                variant={stat.variant || (i % 2 === 0 ? 'copper' : 'forest')}
                delay={i * 0.08}
                className={stat.className}
              />
            ))}
          </div>
        ) : null
      ) : displayStats.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayStats.map((stat, i) => (
            <motion.div
              key={stat.key || stat.label || stat.title || i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`relative group${stat.className ? ` ${stat.className}` : ''}`}
            >
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color || 'from-blue-500 to-blue-600'} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
              <div className="relative overflow-hidden bg-white/10 backdrop-blur-lg rounded-3xl p-4 sm:p-6 border border-white/20">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-white/60 text-sm font-medium truncate">{stat.label || stat.title}</p>
                    <p className="text-2xl font-bold text-white force-white mt-2 tabular-nums">{stat.value}</p>
                  </div>
                  {stat.icon ? (
                    <div className="relative shrink-0">
                      <div className={`hidden sm:block absolute inset-0 bg-gradient-to-br ${stat.color || 'from-blue-500 to-blue-600'} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} />
                      <div className={`relative p-2 rounded-xl bg-gradient-to-r ${stat.color || 'from-blue-500 to-blue-600'}`}>
                        <stat.icon className="h-5 w-5 text-white force-white" strokeWidth={2} />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : null}

      {!qawaem && advancedOptionsContent ? (
        <motion.div
          initial={false}
          animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          {advancedOptionsContent}
        </motion.div>
      ) : null}

      <div className={facturationTableShellClass(qawaem)}>
        {!qawaem ? controlsStrip : null}
        {children}
        {footer}
      </div>
    </div>
  );
}
